'use client';

import { useState } from 'react';
import { LogoSvg } from '@/components/svg/logo';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from '@/hooks/useTranslations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu } from 'lucide-react';
import { withLocaleHref, type Locale } from '@/i18n/config';

const SECTION_LINKS = [
  { hash: '#features-header', key: 'features' },
  { hash: '#benefits', key: 'benefits' },
  { hash: '#how-it-works', key: 'howItWorks' },
  { hash: '#invoice-builder', key: 'tryFree' },
  { hash: '#faq', key: 'faq' },
] as const;

export function LandingHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const locale = (pathname.split('/')[1] || 'en') as Locale;
  const t = useTranslations('LandingPage');
  const isHome = pathname === '/' || pathname === `/${locale}`;
  const baseHref = isHome ? '' : `/${locale}`;

  const sectionLinks = (
    <>
      {SECTION_LINKS.map(({ hash, key }) => (
        <Link
          key={key}
          href={`${baseHref}${hash}`}
          onClick={() => setMobileMenuOpen(false)}
          className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-sm transition-colors block"
        >
          {t(`nav${key.charAt(0).toUpperCase() + key.slice(1)}`)}
        </Link>
      ))}
    </>
  );

  return (
    <header className="sticky top-0 z-40 flex justify-between items-center gap-6 px-5 py-5 md:px-10 w-full bg-header/70 backdrop-blur-xl border-b border-border/20">
      <Link
        href={withLocaleHref('/', locale)}
        aria-label="Invoice Easy"
        className="shrink-0"
      >
        <LogoSvg width="200" height="40" />
      </Link>

      <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
        {sectionLinks}
      </nav>

      <div className="flex items-center gap-3 shrink-0 ml-auto">
        <div className="md:hidden">
          <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Navigation menu">
                <Menu className="w-8 h-8" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48 p-2">
              {sectionLinks}
              <div className="border-t mt-2 pt-2 flex flex-col gap-1">
                <Link
                  href={withLocaleHref('/login', locale)}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-sm transition-colors"
                >
                  {t('navLogin')}
                </Link>
                <Button
                  asChild
                  variant="brand"
                  size="default"
                  className="w-full justify-center rounded-xl"
                >
                  <Link
                    href={withLocaleHref('/register', locale)}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('navSignup')}
                  </Link>
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <Link
            href={withLocaleHref('/login', locale)}
            className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-sm transition-colors"
          >
            {t('navLogin')}
          </Link>
          <Button asChild variant="brand" size="default" className="rounded-xl">
            <Link href={withLocaleHref('/register', locale)}>
              {t('navSignup')}
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
